// dependencies
import { Child } from 'pym.js';
import Zion from './utils/Zion';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

// polyfills
import './utils/polyfills';

// styles
import './styles/graphic.scss';

/* develblock:start */
import * as d3 from 'd3';
import { format } from 'd3-format';
import { mean } from 'd3-array';
import meta from './data/meta.json';
import { linearAxis } from '../../../factory/axes';

import './styles/graphic.scss';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

// Update dayjs locale settings
dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

// Set D3 default time format
d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  shortMonths: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
});

// Formatting helpers
const fmtComma = format(',');
const fmtSI = format('.1s');
const fmtThou = format('~s');
const fmt1f = format('.1f');
const fmtM = n => `${(n / 1000000).toFixed(1)}M`;

// Package info
const pkg = require('../package.json');

// Clear console in development
if (process.env.NODE_ENV === 'development') {
  console.clear();
}

// Global chart variables
let minValue;
let maxValue;
let maxItem;
let maxIndex;
let shortTimestamp;
let medTimestamp;
let summerAvgMean;
let summerAvgStartYear;
let summerAvgEndYear;
let tooltipEl;

// Number of prior summers to include in the rolling average
const SUMMER_AVG_YEARS = 3;

// Global data variables
let data;
let realMax;

// --- NEW: Declare these globals so they are accessible in draw() ---
let daysToUse;
let useSmallTime;

// Initialize pymChild for responsive iframe sizing
window.pymChild = new Child({ id: pkg.name });

// On window load
window.onload = () => {
  // In development, update DOM with meta info
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  // Send initial height
  const { pymChild } = window;
  pymChild.sendHeight();

  // Instantiate Zion if parentUrl exists
  if (window.pymChild.parentUrl) {
    new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }

  // Parse URL parameters and assign globals for days and time flag
  const params = window.location.search.substring(1);
  const searchParams = new URLSearchParams(params);
  const defaultDays = 30;
  const daysParam = searchParams.get('days');
  const isValidDays = +daysParam > 6 && +daysParam < 91;
  daysToUse = isValidDays ? +daysParam : defaultDays;
  useSmallTime = !!searchParams.get('sm');

  // Load the JSON data
  d3.json('https://ix.cnn.io/data/heat-days/people_in_excessive_heat_archive.json').then((jsondata) => {
    let curYear = '';

    // Roll a window over the last SUMMER_AVG_YEARS full summers (e.g. currentYear-3 to currentYear-1)
    const currentYear = +dayjs().format('YYYY');
    summerAvgEndYear = currentYear - 1;
    summerAvgStartYear = currentYear - SUMMER_AVG_YEARS;

    // Filter to the June 20-Sept 22 window within each of those summer years, then pool and average
    const historicalData = jsondata.data.filter((d) => {
      const yr = +d.date.split('-')[0];
      if (yr < summerAvgStartYear || yr > summerAvgEndYear) return false;
      const date = dayjs(d.date);
      const startDate = dayjs(`${yr}-06-20`);
      const endDate = dayjs(`${yr}-09-22`);
      return date.isAfter(startDate) && date.isBefore(endDate);
    });
    summerAvgMean = Math.round(mean(historicalData, d => d.total));

    // Process and map the data
    data = jsondata.data.map((d) => {
      const dayjs_date = dayjs.utc(d.date);
      const yr = d.date.split('-')[0];
      const hasYear = yr !== curYear;
      curYear = yr;
      return {
        ...d,
        date: d3.timeParse('%Y-%m-%d')(d.date),
        year: yr,
        hasYear,
        amt: d.total,
        label: dayjs_date.utc().format('MMM D, YYYY'),
        month: dayjs_date.utc().format('M'),
        month_year: dayjs_date.utc().format('YYYY-MM'),
        full_date: dayjs_date.utc().format('YYYY-MM-DD'),
        date_str: dayjs_date.utc().format('YYYYMMDD'),
      };
    })
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(-daysToUse);

    // Calculate the true maximum from your data
    realMax = d3.max(data, d => d.amt);
    if (realMax === 0) {
      // Update the dek text if no active advisories
      d3.select('.dek')
        .text('No residents in the US are living under active NWS extreme heat advisories, watches or warnings.');
    }

    // Set up y-axis domain (force nonzero max if necessary)
    minValue = Math.min(0, d3.min(data, d => d.amt));
    const maxActualValue = realMax <= 0 ? 1 : realMax;
    maxValue = maxActualValue * 1.2;

    // Determine the max value item for annotation purposes
    maxItem = data.find(d => +d.amt === maxActualValue);
    maxIndex = data.findIndex(d => +d.amt === maxActualValue);

    // Set timestamps for annotation
    const tz = 'America/New_York';
    shortTimestamp = dayjs(jsondata.lastUpdated).tz(tz).format('h a [ET]');
    medTimestamp = dayjs(jsondata.lastUpdated).tz(tz).format('MMMM D');

    // Update the timestamp footnote
    d3.select('p#timestamp')
      .html(`Data collected four times daily. Last updated ${dayjs(jsondata.lastUpdated)
        .tz('America/New_York')
        .format('MMMM D, YYYY [at] h a [ET]')}.`);

    // Draw the chart
    draw();

    // Redraw on window resize
    window.onresize = draw;
  });
};

// Draw function – creates and updates the chart
function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;

  const margins = {
    top: 30,
    right: isMobile ? 60 : 55,
    bottom: 25,
    left: isMobile ? 40 : 40,
  };
  const width = initialWidth - margins.right - margins.left;
  const height = 400;
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  // Create time scale for x-axis
  const x = d3.scaleUtc()
    .domain(d3.extent(data, d => d.date))
    .range([10, chartWidth]);
  const xTicks = isMobile ? 3 : 6;
  const xAxis = d3.axisBottom(x)
    .ticks(xTicks)
    .tickFormat(d => `${dayjs(d).utc().format('MMM D')}`);

  // Recalculate min and max for y-axis
  const minActualValue = d3.min(data, d => d.amt);
  let maxActualValue = d3.max(data, d => d.amt);
  if (maxActualValue <= 0) {
    maxActualValue = 1; // force a small domain if needed
  }
  minValue = Math.min(0, minActualValue);
  maxValue = maxActualValue * 1.2;

  // Set up y-axis scale using linearAxis helper
  const yAxisConfig = {
    orientation: 'left',
    range: [chartHeight, 0],
    domain: [minValue, maxValue],
    nice: false,
  };
  const { axis: yAxis, scale: y } = linearAxis(yAxisConfig);

  const yTicks = isMobile ? 5 : 5;
  const leading = 1.3;
  const formatNum = amt => fmtM(amt);

  // Helper: Draw annotation text and line
  const drawAnnotation = (item, barWidth, classList, align, toplines) => {
    const xOffsetMobileStart = -4;
    const xOffsetDesktopStart = -12;
    const xOffsetEnd = 2.4;
    const isMobile = window.innerWidth <= 500;
    const xOffset = align === 'start'
      ? (isMobile ? xOffsetMobileStart : xOffsetDesktopStart)
      : xOffsetEnd;
    const textAnchor = align === 'start' ? 'start' : 'end';
    const annotation = g.append('g')
      .attr('class', `annotation halo ${classList}`)
      .attr('transform', d => `translate(${x(item.date) + barWidth / 2}, ${y(item.amt < 0 ? 0 : item.amt)})`);

    // Draw the annotation line
    const annoLine = (barWidth / 2) * -1;
    annotation.append('line')
      .attr('x1', annoLine)
      .attr('y1', -25)
      .attr('x2', annoLine)
      .attr('y2', -1)
      .style('stroke-width', 1)
      .style('stroke', '#999')
      .style('fill', 'none');

    // Add annotation text lines if provided
    if (toplines) {
      toplines.forEach((d, i) => {
        const cls = `annotation-date ${(d[1]) ? d[1] : ''}`;
        const txt = d[0];
        const yOffset = d[2] || `${-2.5 - ((i + 1) * leading)}em`;
        annotation.append('text')
          .attr('dx', '10px')
          .attr('class', cls)
          .text(txt)
          .attr('dy', yOffset)
          .attr('dx', xOffset)
          .attr('text-anchor', textAnchor);
      });
    }

    // Append annotation value text
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-value')
      .text(fmtM(item.amt))
      .attr('dy', '-2.5em')
      .attr('dx', xOffset)
      .attr('text-anchor', textAnchor);
  };

  // Create SVG container and group
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);
  const g = svg.append('g')
    .attr('transform', `translate(${margins.left}, ${margins.top})`);

  // Calculate bar width based on available chart width
  const barWidth = chartWidth / data.length * 0.8;

  // Create x-axis grid
  const xGrid = g.append('g')
    .attr('class', 'x grid')
    .attr('transform', `translate(${(barWidth / 3)}, ${chartHeight})`)
    .call(xAxis);
  xGrid.selectAll('text').attr('y', 10);

  // Custom tick formatter for y-axis labels
  function axisFormat(d) {
    const absVal = Math.abs(d);
    const prefix = d < 0 ? '-' : '';
    if (absVal >= 1e9) {
      return `${prefix + Math.round(absVal / 1e9)}B`;
    } if (absVal >= 1e6) {
      return `${prefix + Math.round(absVal / 1e6)}M`;
    } if (absVal >= 1e3) {
      return `${prefix + Math.round(absVal / 1e3)}K`;
    }
    return prefix + Math.round(absVal);
  }

  // Create y-axis grid with custom formatting
  const yGrid = g.append('g')
    .attr('class', 'y grid')
    .call(
      yAxis(y)
        .ticks(yTicks)
        .tickSize(-width)
        .tickFormat(axisFormat),
    );
  yGrid.selectAll('text')
    .attr('x', -5)
    .filter(d => d === 0 && minValue >= 0);

  // Helper: Determine mobile sizing and define tooltip behavior
  const mobileSize = () => window.innerWidth <= 500;
  const tooltip = (d) => {
    if (!tooltipEl) return;
    if (d) {
      tooltipEl.text(formatNum(d.amt));
      const tooltipElOffset = mobileSize() ? 5 : 10;
      tooltipEl.attr('transform', `translate(${x(d.date) + (barWidth / 2) - tooltipElOffset}, ${y(+d.amt)})`);
      tooltipEl.style('visibility', 'visible');
    } else {
      tooltipEl.text('');
      tooltipEl.style('visibility', 'hidden');
    }
  };

  // Draw the bars
  const group = g.append('g').attr('class', 'data');
  const bars = group.append('g').attr('class', 'bars');
  const barSelection = bars.selectAll('.bar')
    .data(data)
    .enter().append('g')
    .attr('class', (d, i) => (i === maxIndex || i === data.length - 1 ? 'bar highlight' : 'bar'))
    .attr('role', 'list')
    .attr('transform', d => `translate(${x(d.date) - barWidth / 2}, ${y(d.amt < 0 ? 0 : d.amt)})`)
    .on('mouseover', (d, i) => {
      if (i !== maxIndex && i < data.length - 1) tooltip(d);
      else tooltip();
    })
    .on('mouseout', () => tooltip());

  // Add title and rect for each bar
  barSelection.append('title')
    .text(d => `${d.label} (${fmtM(d.amt)})`);
  barSelection.append('rect')
    .attr('width', barWidth)
    .attr('height', d => Math.abs(y(d.amt) - y(0)));

  // Draw zero-line if needed
  if (y.domain()[0] < 0) {
    g.append('line')
      .attr('role', 'presentation')
      .attr('class', 'zero-bar')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', y(0))
      .attr('y2', y(0));
  }

  // Draw historical mean line and label
  g.append('line')
    .attr('role', 'presentation')
    .attr('class', 'zero-bar historical-mean')
    .style('stroke-dasharray', '3, 4')
    .attr('x1', 0)
    .attr('x2', width)
    .attr('y1', y(summerAvgMean))
    .attr('y2', y(summerAvgMean));
  g.append('text')
    .attr('x', 4)
    .attr('y', y(summerAvgMean))
    .attr('dy', '-.6em')
    .attr('class', 'historical-label halo')
    .text(`${summerAvgStartYear}-${summerAvgEndYear} summer avg.`);

  // Conditionally draw the 30-day high annotation (skip if no active advisories)
  if (maxIndex < data.length - 1 && realMax !== 0) {
    const align = maxIndex >= data.length / 2 ? 'end' : 'start';
    const annotationHigh = [
      [maxItem.label.split(',')[0]],
      [`${daysToUse}-day high`],
    ];
    drawAnnotation(maxItem, barWidth, 'max', align, annotationHigh);
  }

  // Draw current/latest annotation based on the useSmallTime flag
  if (useSmallTime) {
    drawAnnotation(data[data.length - 1], barWidth, 'current', 'start', [
      [shortTimestamp, 'timestamp', -45],
      ['Latest', '', -57],
    ]);
  } else {
    drawAnnotation(data[data.length - 1], barWidth, 'current', 'start', [
      [medTimestamp],
    ]);
  }

  // Create tooltip element
  tooltipEl = svg.append('g')
    .attr('transform', `translate(${margins.left}, ${margins.top})`)
    .append('text')
    .attr('dy', '-.5em')
    .attr('class', 'value in halo tooltipTxt')
    .attr('text-anchor', 'middle')
    .text('');

  // Update iframe height and mark as ready
  window.pymChild.sendHeight();
  d3.select('#root').attr('class', 'ready');
}
